import { useState } from "react";

import { useCriarMedicamento, useEditarMedicamento, useMedicamentos } from "@/hooks/useApi";
import { useUser } from "@/hooks/useUser";
import type { Categoria, Medicamento } from "@/types/api";

const CATEGORIA_LABEL: Record<Categoria, string> = {
  ordinario: "Ordinário",
  alto_custo: "Alto custo",
  estoque_obrigatorio: "Estoque obrigatório",
};

const RECEITUARIOS = ["A1", "A2", "A3", "B1", "B2", "C1", "C2", "C3", "C4", "C5"];

export function MedicamentosPage() {
  const { data: user } = useUser();
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState<Categoria | "">("");
  const [editar, setEditar] = useState<Medicamento | null>(null);
  const [novo, setNovo] = useState(false);
  const { data, isLoading, error } = useMedicamentos({
    q: q || undefined,
    categoria: categoria || undefined,
  });

  const podeEditar = user && ["admin", "compras", "farmaceutico"].includes(user.papel);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Catálogo de medicamentos</h2>
          <p className="text-sm text-ink-muted">Medicamentos ativos · busque, edite ou cadastre um novo.</p>
        </div>
        {podeEditar ? (
          <button className="btn-primary" onClick={() => setNovo(true)}>+ Novo medicamento</button>
        ) : null}
      </div>

      <div className="flex gap-3">
        <input
          className="input max-w-sm"
          placeholder="Buscar por descrição ou código G…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="input max-w-xs"
          value={categoria}
          onChange={(e) => setCategoria(e.target.value as Categoria | "")}
        >
          <option value="">Todas as categorias</option>
          <option value="ordinario">Ordinário</option>
          <option value="alto_custo">Alto custo</option>
          <option value="estoque_obrigatorio">Estoque obrigatório</option>
        </select>
      </div>

      {isLoading ? <p className="text-ink-muted">Carregando…</p> : null}
      {error ? <p className="text-anarosa">Erro ao carregar catálogo.</p> : null}

      {data ? (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Descrição</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3">Port. 344</th>
                <th className="px-4 py-3 text-right">Mín.</th>
                {podeEditar ? <th className="px-4 py-3"></th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-ink-muted">{m.codigo_interno}</td>
                  <td className="px-4 py-2">{m.descricao}</td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        m.categoria === "alto_custo" ? "badge-alto-custo" : "badge-ordinario"
                      }
                    >
                      {CATEGORIA_LABEL[m.categoria]}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-ink-muted">
                    {m.portaria_344 ? (
                      <span className="badge bg-amber-100 text-amber-800">
                        344{m.tipo_receituario_344 ? ` · ${m.tipo_receituario_344}` : ""}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2 text-right">{m.estoque_minimo}</td>
                  {podeEditar ? (
                    <td className="px-4 py-2 text-right">
                      <button className="text-sm text-anarosa hover:underline" onClick={() => setEditar(m)}>
                        Editar
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
              {data.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-ink-muted" colSpan={podeEditar ? 6 : 5}>
                    Nenhum resultado.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}

      {editar ? <EditarModal med={editar} onClose={() => setEditar(null)} /> : null}
      {novo ? <NovoMedicamentoModal onClose={() => setNovo(false)} /> : null}
    </div>
  );
}

function NovoMedicamentoModal({ onClose }: { onClose: () => void }) {
  const criar = useCriarMedicamento();
  const [f, setF] = useState({
    descricao: "",
    codigo_barras_ean: "",
    codigo_anvisa: "",
    codigo_interno: "",
    categoria: "ordinario" as Categoria,
    portaria_344: false,
    tipo_receituario_344: "",
    apresentacao: "",
    unidade_medida: "",
    estoque_minimo: "0",
  });
  function set<K extends keyof typeof f>(k: K, v: (typeof f)[K]) {
    setF((s) => ({ ...s, [k]: v }));
  }
  const ok = f.descricao.trim().length >= 2 &&
    (!f.codigo_barras_ean || /^\d{8,14}$/.test(f.codigo_barras_ean)) &&
    (!f.portaria_344 || !!f.tipo_receituario_344);

  async function salvar() {
    if (!ok) return;
    try {
      await criar.mutateAsync({
        descricao: f.descricao.trim(),
        codigo_barras_ean: f.codigo_barras_ean.trim() || null,
        codigo_anvisa: f.codigo_anvisa.trim() || null,
        codigo_interno: f.codigo_interno.trim() || null,
        categoria: f.categoria,
        portaria_344: f.portaria_344,
        tipo_receituario_344: f.portaria_344 ? (f.tipo_receituario_344 || null) : null,
        apresentacao: f.apresentacao.trim() || null,
        unidade_medida: f.unidade_medida.trim() || null,
        estoque_minimo: Number(f.estoque_minimo) || 0,
      });
      setTimeout(onClose, 1000);
    } catch { /* erro via criar.error */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="card max-h-[88vh] w-full max-w-lg overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold">Novo medicamento</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Descrição <span className="text-anarosa">*</span></label>
            <input className="input mt-1" autoFocus placeholder="ex.: PROPOFOL 10MG/ML 20ML — PROPOVAN"
              value={f.descricao} onChange={(e) => set("descricao", e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">EAN (código de barras)</label>
              <input className="input mt-1" inputMode="numeric" placeholder="7896…"
                value={f.codigo_barras_ean} onChange={(e) => set("codigo_barras_ean", e.target.value)} />
              {f.codigo_barras_ean && !/^\d{8,14}$/.test(f.codigo_barras_ean)
                ? <p className="text-xs text-anarosa mt-0.5">EAN deve ter 8–14 dígitos.</p> : null}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Código ANVISA</label>
              <input className="input mt-1" placeholder="1029801340121"
                value={f.codigo_anvisa} onChange={(e) => set("codigo_anvisa", e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-ink-subtle -mt-1">EAN ou ANVISA fazem a NF casar com este item na conferência.</p>

          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Categoria</label>
            <select className="input mt-1" value={f.categoria} onChange={(e) => set("categoria", e.target.value as Categoria)}>
              <option value="ordinario">Ordinário</option>
              <option value="alto_custo">Alto custo</option>
              <option value="estoque_obrigatorio">Estoque obrigatório</option>
            </select>
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <input type="checkbox" checked={f.portaria_344} onChange={(e) => set("portaria_344", e.target.checked)} />
              Controlado (Portaria 344)
            </label>
            {f.portaria_344 ? (
              <div>
                <label className="text-xs uppercase tracking-wider text-amber-800">Tipo de receituário <span className="text-anarosa">*</span></label>
                <select className="input mt-1" value={f.tipo_receituario_344} onChange={(e) => set("tipo_receituario_344", e.target.value)}>
                  <option value="">— selecione —</option>
                  {RECEITUARIOS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="text-xs text-amber-800 mt-0.5">A1–A3/B1–B2: notificação + retenção de receita na dispensação. C1–C5: prescritor + CRM.</p>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Apresentação</label>
              <input className="input mt-1" placeholder="amp 20ml, fr 50ml…" value={f.apresentacao} onChange={(e) => set("apresentacao", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Unidade</label>
              <input className="input mt-1" placeholder="amp, fr, comp…" value={f.unidade_medida} onChange={(e) => set("unidade_medida", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Estoque mínimo</label>
              <input className="input mt-1" type="number" min={0} value={f.estoque_minimo} onChange={(e) => set("estoque_minimo", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Código interno (opc.)</label>
              <input className="input mt-1" placeholder="auto (MAN-####)" value={f.codigo_interno} onChange={(e) => set("codigo_interno", e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button className="btn-primary" disabled={!ok || criar.isPending || criar.isSuccess} onClick={salvar}>
              {criar.isPending ? "Cadastrando…" : "Cadastrar"}
            </button>
            {criar.error ? <span className="text-anarosa text-sm">{(criar.error as Error).message}</span> : null}
            {criar.isSuccess ? <span className="text-green-700 text-sm">✅ Cadastrado.</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditarModal({ med, onClose }: { med: Medicamento; onClose: () => void }) {
  const { data: user } = useUser();
  const editar = useEditarMedicamento();
  const isFarma = user?.papel === "farmaceutico";
  // farmacêutico não altera categoria/ativo
  const [form, setForm] = useState({
    categoria: med.categoria,
    portaria_344: med.portaria_344,
    tipo_receituario_344: med.tipo_receituario_344 ?? "",
    codigo_barras_ean: med.codigo_barras_ean ?? "",
    principio_ativo: med.principio_ativo ?? "",
    apresentacao: med.apresentacao ?? "",
    unidade_medida: med.unidade_medida ?? "",
    estoque_minimo: String(med.estoque_minimo),
  });

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function salvar() {
    const patch: Partial<Medicamento> = {
      portaria_344: form.portaria_344,
      tipo_receituario_344: form.portaria_344 ? (form.tipo_receituario_344 || null) : null,
      codigo_barras_ean: form.codigo_barras_ean || null,
      principio_ativo: form.principio_ativo || null,
      apresentacao: form.apresentacao || null,
      unidade_medida: form.unidade_medida || null,
      estoque_minimo: Number(form.estoque_minimo),
    };
    if (!isFarma) patch.categoria = form.categoria;
    try {
      await editar.mutateAsync({ id: med.id, patch });
      setTimeout(onClose, 1200);
    } catch { /* erro via editar.error */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="card max-h-[88vh] w-full max-w-lg overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold">Editar medicamento</h3>
            <p className="text-sm text-ink-muted"><code className="text-xs">{med.codigo_interno}</code> {med.descricao}</p>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink">✕</button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-ink-muted">Categoria</label>
            <select
              className="input mt-1"
              value={form.categoria}
              disabled={isFarma}
              onChange={(e) => set("categoria", e.target.value as Categoria)}
            >
              <option value="ordinario">Ordinário</option>
              <option value="alto_custo">Alto custo</option>
              <option value="estoque_obrigatorio">Estoque obrigatório</option>
            </select>
            {isFarma ? <p className="text-xs text-ink-subtle mt-0.5">Categoria só admin/compras alteram.</p> : null}
          </div>

          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <input
                type="checkbox"
                checked={form.portaria_344}
                onChange={(e) => set("portaria_344", e.target.checked)}
              />
              Medicamento sujeito a Portaria 344 (controlado)
            </label>
            {form.portaria_344 ? (
              <div>
                <label className="text-xs uppercase tracking-wider text-amber-800">Tipo de receituário</label>
                <select
                  className="input mt-1"
                  value={form.tipo_receituario_344}
                  onChange={(e) => set("tipo_receituario_344", e.target.value)}
                >
                  <option value="">— selecione —</option>
                  {RECEITUARIOS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="text-xs text-amber-800 mt-0.5">A1–A3/B1–B2 exigem notificação + retenção de receita na dispensação.</p>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">EAN-13</label>
              <input className="input mt-1" value={form.codigo_barras_ean} onChange={(e) => set("codigo_barras_ean", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Estoque mínimo</label>
              <input className="input mt-1" type="number" min={0} value={form.estoque_minimo} onChange={(e) => set("estoque_minimo", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Princípio ativo</label>
              <input className="input mt-1" value={form.principio_ativo} onChange={(e) => set("principio_ativo", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Apresentação</label>
              <input className="input mt-1" value={form.apresentacao} onChange={(e) => set("apresentacao", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink-muted">Unidade</label>
              <input className="input mt-1" value={form.unidade_medida} placeholder="amp, comp, fr…" onChange={(e) => set("unidade_medida", e.target.value)} />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button className="btn-primary" disabled={editar.isPending || editar.isSuccess} onClick={salvar}>
              {editar.isPending ? "Salvando…" : "Salvar"}
            </button>
            {editar.error ? <span className="text-anarosa text-sm">{(editar.error as Error).message}</span> : null}
            {editar.isSuccess ? <span className="text-green-700 text-sm">✅ Salvo.</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
